import { useCallback, useEffect, useState } from "react";

import { isProfileSubmenuTab } from "../navConfig";
import type { ClientTab } from "../urlState";

const SUBMENU_KEY = "clientProfile.nav.profileExpanded";

const read = (key: string, fallback: boolean): boolean => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw === "true";
  } catch {
    return fallback;
  }
};

const write = (key: string, value: boolean) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
};

export type ClientNavState = {
  /** Whether the rail is shown right now (preference minus any flow collapse). */
  isOpen: boolean;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  /**
   * Hide the rail for the rest of the current Requests flow without touching
   * the remembered preference (the intake wizard wants the width).
   */
  collapseForFlow: () => void;
  /** The Profile submenu's expanded state. */
  isProfileExpanded: boolean;
  setProfileExpanded: (expanded: boolean) => void;
  /** The Requests row's tree; opens with the Requests tab and folds when another tab opens. */
  isRequestsExpanded: boolean;
  setRequestsExpanded: (expanded: boolean) => void;
};

/**
 * Rail visibility and the Profile submenu. The rail opens on every visit to a
 * client (hiding it lasts until the user leaves that client); the submenu is
 * remembered across visits and also opens itself whenever one of its
 * children becomes active.
 */
export const useClientNavState = (
  activeTab: ClientTab,
  clientId: string
): ClientNavState => {
  const [preferOpen, setPreferOpen] = useState(true);
  const [flowCollapsed, setFlowCollapsed] = useState(false);
  const [navClientId, setNavClientId] = useState(clientId);
  if (navClientId !== clientId) {
    setNavClientId(clientId);
    setPreferOpen(true);
    setFlowCollapsed(false);
  }
  const [isProfileExpanded, setIsProfileExpanded] = useState(
    () => read(SUBMENU_KEY, false) || isProfileSubmenuTab(activeTab)
  );

  useEffect(() => {
    if (isProfileSubmenuTab(activeTab)) setIsProfileExpanded(true);
  }, [activeTab]);

  const [isRequestsExpanded, setRequestsExpanded] = useState(
    activeTab === "outgoing_intakes"
  );
  useEffect(() => {
    setRequestsExpanded(activeTab === "outgoing_intakes");
  }, [activeTab]);

  // Leaving Requests ends any flow-scoped collapse.
  useEffect(() => {
    if (activeTab !== "outgoing_intakes") setFlowCollapsed(false);
  }, [activeTab]);

  const setOpen = useCallback((open: boolean) => {
    setFlowCollapsed(false);
    setPreferOpen(open);
  }, []);

  const isOpen = preferOpen && !flowCollapsed;

  const toggle = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen, setOpen]);

  const collapseForFlow = useCallback(() => setFlowCollapsed(true), []);

  const setProfileExpanded = useCallback((expanded: boolean) => {
    setIsProfileExpanded(expanded);
    write(SUBMENU_KEY, expanded);
  }, []);

  return {
    isOpen,
    toggle,
    setOpen,
    collapseForFlow,
    isProfileExpanded,
    setProfileExpanded,
    isRequestsExpanded,
    setRequestsExpanded
  };
};
